'use client';

import type React from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import type { CertificateType } from '../components/certification-wizard';

interface CertificationContextType {
  // Certificate selection
  selectedCertificate: CertificateType | null;
  setSelectedCertificate: (certificate: CertificateType | null) => void;

  // Step completion status
  videosCompleted: number[];
  setVideosCompleted: (id: number[]) => void;
  contentsReviewed: number[];
  setContentsReviewed: (reviewed: number[]) => void;
  quizCompleted: boolean;
  setQuizCompleted: (completed: boolean) => void;

  // User information
  userName: string;
  setUserName: (name: string) => void;
  signature: string | null;
  setSignature: (signature: string | null) => void;

  // Quiz state
  quizScore: number;
  setQuizScore: (score: number) => void;

  // Progress tracking
  currentStep: number;
  setCurrentStep: (step: number) => void;
  totalSteps: number;
  latestCompletedStep: number;

  // Certificate generation
  certificateUrl: string | null;
  setCertificateUrl: (url: string | null) => void;
  certificateId: string | null;
  setCertificateId: (id: string | null) => void;

  // Firebase storage
  firebaseUrl: string | null;
  setFirebaseUrl: (url: string | null) => void;
  isUploading: boolean;
  setIsUploading: (uploading: boolean) => void;

  // Timestamps
  startTime: Date | null;
  completionTime: Date | null;
  setCompletionTime: (time: Date | null) => void;

  // Reset function
  resetCertification: () => void;
}

const CertificationContext = createContext<CertificationContextType | undefined>(undefined);

export function CertificationProvider({ children }: { children: React.ReactNode }) {
  // Certificate selection
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateType | null>(null);

  // Step completion status
  const [videosCompleted, setVideosCompleted] = useState<number[]>([]);
  const [contentsReviewed, setContentsReviewed] = useState<number[]>([]);
  const [quizCompleted, setQuizCompleted] = useState(false);

  // User information
  const [userName, setUserName] = useState('');
  const [signature, setSignature] = useState<string | null>(null);

  // Quiz state
  const [quizScore, setQuizScore] = useState(0);

  // Progress tracking
  const [currentStep, setCurrentStep] = useState(1);
  const [latestCompletedStep, setLatestCompletedStep] = useState(0);
  const totalSteps = 5;

  // Certificate generation
  const [certificateUrl, setCertificateUrl] = useState<string | null>(null);
  const [certificateId, setCertificateId] = useState<string | null>(null);

  // Firebase storage
  const [firebaseUrl, setFirebaseUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Timestamps
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [completionTime, setCompletionTime] = useState<Date | null>(null);

  useEffect(() => {
    if (currentStep > latestCompletedStep) {
      setLatestCompletedStep(currentStep);
    }
  }, [currentStep]);

  // Set start time when component mounts
  useEffect(() => {
    setStartTime(new Date());
  }, []);

  // Reset all state
  const resetCertification = () => {
    setSelectedCertificate(null);
    setVideosCompleted([]);
    setContentsReviewed([]);
    setQuizCompleted(false);
    setUserName('');
    setSignature(null);
    setQuizScore(0);
    setCurrentStep(1);
    setCertificateUrl(null);
    setCertificateId(null);
    setFirebaseUrl(null);
    setIsUploading(false);
    setStartTime(new Date());
    setCompletionTime(null);
  };

  // When changing certificate, reset progress
  useEffect(() => {
    if (selectedCertificate) {
      setVideosCompleted([]);
      setContentsReviewed([]);
      setQuizCompleted(false);
      setCertificateUrl(null);
      setCertificateId(null);
      setFirebaseUrl(null);
    }
  }, [selectedCertificate]);

  const value = {
    selectedCertificate,
    setSelectedCertificate,
    videosCompleted,
    setVideosCompleted,
    contentsReviewed,
    setContentsReviewed,
    quizCompleted,
    setQuizCompleted,
    userName,
    setUserName,
    signature,
    setSignature,
    quizScore,
    setQuizScore,
    currentStep,
    setCurrentStep,
    totalSteps,
    latestCompletedStep,
    certificateUrl,
    setCertificateUrl,
    certificateId,
    setCertificateId,
    firebaseUrl,
    setFirebaseUrl,
    isUploading,
    setIsUploading,
    startTime,
    completionTime,
    setCompletionTime,
    resetCertification,
  };

  return <CertificationContext.Provider value={value}>{children}</CertificationContext.Provider>;
}

export function useCertification() {
  const context = useContext(CertificationContext);
  if (context === undefined) {
    throw new Error('useCertification must be used within a CertificationProvider');
  }
  return context;
}
